import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  addPartnerLeadNote,
  getPartnerLead,
  listPartnerLeads,
  listPartnerResponseTemplates,
  updatePartnerLeadStatus,
} from "../../api/partnerPortalApi";
import { usePartnerAuth } from "../../auth/PartnerAuthProvider";
import { usePartnerPortalBase } from "../../components/partner/PartnerPortalBaseProvider";
import { useLocale } from "../../intl";
import { isCasaMapaVendorPartner } from "../../utils/casamapaPartnerPortalTabs";
import {
  buildLeadSearchHaystack,
  formatLeadDate,
  leadCategory,
  leadDisplayName,
  leadTypeI18nKey,
  parseLeadMetadata,
  partnerLeadStatusTone,
  PARTNER_LEAD_ACTIVE_STATUSES,
  PARTNER_LEAD_CLOSED_STATUSES,
  relativeLeadAge,
  summarizePartnerLeads,
  whatsappHref,
} from "../../utils/casamapaPartnerLeadsPresentation";
import "../../styles/partnerPortalDashboard.css";
import "../../styles/partnerCasaMapaLeads.css";

const COPY = "realEstate.casaMapa.admin.partnerLeads";

const STATUS_FILTER_VALUES = ["", "new", "active", "closed"];
const SLA_HOURS = 4;

function slaState(leadOrDetail) {
  const status = leadOrDetail?.partnerStatus || leadOrDetail?.partner_status;
  if (status !== "new") return null;
  const assignedAt =
    leadOrDetail?.assignedAt ||
    leadOrDetail?.assigned_at ||
    leadOrDetail?.createdAt ||
    leadOrDetail?.lead_created_at;
  if (!assignedAt) return null;
  const ageMs = Date.now() - new Date(assignedAt).getTime();
  if (!Number.isFinite(ageMs) || ageMs < 0) return null;
  const hours = ageMs / (1000 * 60 * 60);
  if (hours >= SLA_HOURS) return "overdue";
  if (hours >= SLA_HOURS / 2) return "warning";
  return "ok";
}

function PipelineBar({ label, value, max, tone = "blue" }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="cm-dashboard__pipeline-row">
      <div className="cm-dashboard__pipeline-head">
        <span>{label}</span>
        <strong>{value}</strong>
      </div>
      <div className="cm-dashboard__pipeline-track" aria-hidden="true">
        <div
          className={`cm-dashboard__pipeline-fill cm-dashboard__pipeline-fill--${tone}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function KpiCard({ label, value, hint, tone = "slate" }) {
  return (
    <article className={`partner-kpi-card partner-kpi-card--${tone}`}>
      <span className="partner-kpi-card__label">{label}</span>
      <strong className="partner-kpi-card__value">{value ?? "—"}</strong>
      {hint ? <small className="partner-kpi-card__hint">{hint}</small> : null}
    </article>
  );
}

function LeadStatusBadge({ status, t }) {
  return (
    <span className={`partner-lead-badge partner-lead-badge--${partnerLeadStatusTone(status)}`}>
      {t(`${COPY}.status.${status}`, { defaultValue: status })}
    </span>
  );
}

function LeadCategoryBadge({ leadType, t }) {
  const category = leadCategory(leadType);
  return (
    <span className={`cm-partner-leads__type-badge cm-partner-leads__type-badge--${category}`}>
      {t(`${COPY}.categories.${category}`)}
    </span>
  );
}

function LeadListItem({ lead, selected, onSelect, t, locale }) {
  const meta = parseLeadMetadata(lead);
  const contextLine =
    lead.listing_title ||
    meta.listingTitle ||
    meta.listing_title ||
    meta.serviceCategory ||
    meta.vendorName ||
    t(`${COPY}.leadTypes.${leadTypeI18nKey(lead.lead_type)}`);
  const ageKey = relativeLeadAge(lead.lead_created_at, locale);
  const ageLabel =
    ageKey && ["just_now", "today", "yesterday", "this_week"].includes(ageKey)
      ? t(`${COPY}.age.${ageKey}`)
      : formatLeadDate(lead.lead_created_at, locale);

  return (
    <button
      type="button"
      className={`partner-lead-item cm-partner-leads__lead-card${
        selected ? " is-selected" : ""
      }${lead.partner_status === "new" ? " is-priority" : ""}`}
      onClick={() => onSelect(lead.id)}
    >
      <div className="partner-lead-item__top">
        <span className="partner-lead-item__number">{lead.lead_number}</span>
        <LeadStatusBadge status={lead.partner_status} t={t} />
      </div>
      <div className="partner-lead-item__top">
        <LeadCategoryBadge leadType={lead.lead_type} t={t} />
        {ageLabel ? <span className="partner-lead-item__date">{ageLabel}</span> : null}
      </div>
      <strong className="partner-lead-item__name">
        {leadDisplayName(lead) || t(`${COPY}.unnamedLead`)}
      </strong>
      <p className="cm-partner-leads__lead-context">{contextLine}</p>
      <p className="partner-lead-item__meta">
        {t(`${COPY}.leadTypes.${leadTypeI18nKey(lead.lead_type)}`)}
      </p>
      {lead.phone ? <span className="partner-lead-item__phone">{lead.phone}</span> : null}
    </button>
  );
}

function LeadDetailEmpty({ t }) {
  return (
    <div className="partner-lead-detail partner-lead-detail--empty cm-partner-leads__empty-state">
      <span className="partner-lead-detail__empty-icon" aria-hidden="true">
        ✉
      </span>
      <h3>{t(`${COPY}.detail.emptyTitle`)}</h3>
      <p>{t(`${COPY}.detail.emptyBody`)}</p>
    </div>
  );
}

function resolveNextStep(status, t) {
  if (status === "new") return t(`${COPY}.detail.nextSteps.new`);
  if (status === "accepted" || status === "contacted") return t(`${COPY}.detail.nextSteps.followUp`);
  if (status === "appointment_set" || status === "working") return t(`${COPY}.detail.nextSteps.working`);
  if (PARTNER_LEAD_CLOSED_STATUSES.includes(status)) return t(`${COPY}.detail.nextSteps.closed`);
  return t(`${COPY}.detail.nextSteps.default`);
}

function LeadDetailPanel({
  detail,
  listMeta,
  note,
  onNoteChange,
  onSubmitNote,
  onStatusChange,
  onApplyTemplate,
  templates,
  saving,
  t,
  locale,
}) {
  const metadata = listMeta || detail.metadata || {};
  const waHref = whatsappHref(detail.phone);
  const intent = metadata.casamapaIntent || metadata.intent;
  const timeline = metadata.timeline;
  const readiness = metadata.readiness;
  const preferredMethod = metadata.preferredContactMethod || metadata.preferredMethod;
  const listingTitle = detail.listingTitle || metadata.listingTitle || metadata.listing_title;
  const serviceLabel = metadata.serviceCategory || metadata.serviceName || metadata.vendorService;
  const sla = slaState(detail);

  return (
    <div className="partner-lead-detail">
      <header className="partner-lead-detail__header">
        <div>
          <p className="partner-lead-detail__eyebrow">{detail.leadNumber}</p>
          <h2>{detail.contactName || t(`${COPY}.unnamedLead`)}</h2>
          <p className="partner-lead-detail__subtitle">
            {t(`${COPY}.leadTypes.${leadTypeI18nKey(detail.leadType)}`)}
          </p>
          {sla ? (
            <p className={`cm-partner-leads__sla cm-partner-leads__sla--${sla}`}>
              {t(`${COPY}.sla.${sla}`)}
            </p>
          ) : detail.firstRespondedAt ? (
            <p className="muted">
              {t(`${COPY}.sla.responded`, {
                date: formatLeadDate(detail.firstRespondedAt, locale),
              })}
            </p>
          ) : null}
        </div>
        <LeadStatusBadge status={detail.partnerStatus} t={t} />
      </header>

      <div className="cm-partner-leads__detail-next">
        <p>{resolveNextStep(detail.partnerStatus, t)}</p>
        <div className="cm-partner-leads__detail-actions">
          {waHref ? (
            <a
              className="partner-dashboard-btn partner-dashboard-btn--primary partner-dashboard-btn--whatsapp"
              href={waHref}
              target="_blank"
              rel="noreferrer"
            >
              {t(`${COPY}.detail.whatsapp`)}
            </a>
          ) : null}
          {detail.partnerStatus === "new" ? (
            <button
              type="button"
              className="partner-dashboard-btn partner-dashboard-btn--primary"
              disabled={saving}
              onClick={() => onStatusChange("contacted")}
            >
              {t(`${COPY}.detail.markContacted`)}
            </button>
          ) : null}
          {["new", "contacted", "accepted"].includes(detail.partnerStatus) ? (
            <button
              type="button"
              className="partner-dashboard-btn"
              disabled={saving}
              onClick={() => onStatusChange("working")}
            >
              {t(`${COPY}.detail.markWorking`)}
            </button>
          ) : null}
          {!PARTNER_LEAD_CLOSED_STATUSES.includes(detail.partnerStatus) ? (
            <button
              type="button"
              className="partner-dashboard-btn"
              disabled={saving}
              onClick={() => onStatusChange("closed_won")}
            >
              {t(`${COPY}.detail.closeWon`)}
            </button>
          ) : null}
        </div>
      </div>

      <div className="partner-lead-detail__grid">
        <section className="partner-lead-detail__card">
          <h3>{t(`${COPY}.detail.contact`)}</h3>
          {detail.contactName ? <p>{detail.contactName}</p> : null}
          {detail.phone ? (
            <p>
              {t(`${COPY}.detail.phone`)}:{" "}
              {waHref ? (
                <a href={waHref} target="_blank" rel="noreferrer">
                  {detail.phone}
                </a>
              ) : (
                detail.phone
              )}
            </p>
          ) : null}
          {detail.email ? (
            <p>
              {t(`${COPY}.detail.email`)}:{" "}
              <a href={`mailto:${detail.email}`}>{detail.email}</a>
            </p>
          ) : null}
          {preferredMethod ? (
            <p className="muted">
              {t(`${COPY}.detail.preferredMethod`)}:{" "}
              {t(`realEstate.casaMapa.lead.preferred.${preferredMethod}`, {
                defaultValue: preferredMethod,
              })}
            </p>
          ) : null}
        </section>

        {listingTitle || serviceLabel ? (
          <section className="partner-lead-detail__card">
            <h3>
              {isAgencyContext(detail.leadType)
                ? t(`${COPY}.detail.propertyContext`)
                : t(`${COPY}.detail.serviceContext`)}
            </h3>
            {listingTitle ? <p>{listingTitle}</p> : null}
            {serviceLabel ? <p>{serviceLabel}</p> : null}
            {detail.pageUrl ? (
              <p>
                <a href={detail.pageUrl} target="_blank" rel="noreferrer">
                  {t(`${COPY}.detail.viewSourcePage`)}
                </a>
              </p>
            ) : null}
          </section>
        ) : null}

        {intent || timeline || readiness ? (
          <section className="partner-lead-detail__card partner-lead-detail__card--wide">
            <h3>{t(`${COPY}.detail.buyerPreferences`)}</h3>
            <div className="cm-partner-leads__meta-grid">
              {intent ? (
                <div className="cm-partner-leads__meta-item">
                  <span>{t(`${COPY}.detail.intent`)}</span>
                  <strong>
                    {t(`realEstate.casaMapa.lead.intents.${intent}`, {
                      defaultValue: intent,
                    })}
                  </strong>
                </div>
              ) : null}
              {timeline ? (
                <div className="cm-partner-leads__meta-item">
                  <span>{t(`${COPY}.detail.timeline`)}</span>
                  <strong>
                    {t(`realEstate.casaMapa.lead.timelines.${timeline}`, {
                      defaultValue: timeline,
                    })}
                  </strong>
                </div>
              ) : null}
              {readiness ? (
                <div className="cm-partner-leads__meta-item">
                  <span>{t(`${COPY}.detail.readiness`)}</span>
                  <strong>
                    {t(`realEstate.casaMapa.lead.readinessOptions.${readiness}`, {
                      defaultValue: readiness,
                    })}
                  </strong>
                </div>
              ) : null}
            </div>
          </section>
        ) : null}

        {detail.message ? (
          <section className="partner-lead-detail__card partner-lead-detail__card--wide">
            <h3>{t(`${COPY}.detail.message`)}</h3>
            <p>{detail.message}</p>
          </section>
        ) : null}

        {detail.createdAt ? (
          <section className="partner-lead-detail__card">
            <h3>{t(`${COPY}.detail.received`)}</h3>
            <p>{formatLeadDate(detail.createdAt, locale)}</p>
          </section>
        ) : null}
      </div>

      <section className="partner-lead-detail__notes">
        <h3>{t(`${COPY}.detail.notes`)}</h3>
        {templates?.length ? (
          <label className="partner-lead-note-field">
            <span>{t(`${COPY}.detail.templates`)}</span>
            <select
              defaultValue=""
              onChange={(event) => {
                const template = templates.find((row) => row.templateId === event.target.value);
                if (template) onApplyTemplate?.(template.body);
                event.target.value = "";
              }}
            >
              <option value="">{t(`${COPY}.detail.templatePlaceholder`)}</option>
              {templates.map((template) => (
                <option key={template.templateId} value={template.templateId}>
                  {template.title}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <label className="partner-lead-note-field">
          <span>{t(`${COPY}.detail.addNote`)}</span>
          <textarea rows={3} value={note} onChange={(event) => onNoteChange(event.target.value)} />
        </label>
        <button
          type="button"
          className="partner-dashboard-btn partner-dashboard-btn--primary"
          disabled={saving || !note.trim()}
          onClick={onSubmitNote}
        >
          {t(`${COPY}.detail.saveNote`)}
        </button>

        {detail.notes?.length ? (
          <ul className="partner-lead-notes-list">
            {detail.notes.map((entry) => (
              <li key={entry.id}>
                <p>{entry.noteText}</p>
                {entry.createdAt ? (
                  <time dateTime={entry.createdAt}>{formatLeadDate(entry.createdAt, locale)}</time>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted">{t(`${COPY}.detail.noNotes`)}</p>
        )}
      </section>
    </div>
  );
}

function isAgencyContext(leadType) {
  return leadCategory(leadType) === "property";
}

function matchesStatusFilter(lead, statusFilter) {
  if (!statusFilter) return true;
  if (statusFilter === "new") return lead.partner_status === "new";
  if (statusFilter === "active") return PARTNER_LEAD_ACTIVE_STATUSES.includes(lead.partner_status);
  if (statusFilter === "closed") return PARTNER_LEAD_CLOSED_STATUSES.includes(lead.partner_status);
  return lead.partner_status === statusFilter;
}

export default function PartnerCasaMapaLeadsPage() {
  const { partner } = usePartnerAuth();
  const { path } = usePartnerPortalBase();
  const { t, locale } = useLocale();
  const isVendor = isCasaMapaVendorPartner(partner);

  const [leads, setLeads] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [templates, setTemplates] = useState([]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage("");
      const [body, templateBody] = await Promise.all([
        listPartnerLeads({ limit: 100 }),
        listPartnerResponseTemplates().catch(() => ({ templates: [] })),
      ]);
      setLeads(body.leads || []);
      setTemplates(templateBody.templates || []);
    } catch (error) {
      setErrorMessage(error.message || t(`${COPY}.errors.load`));
      setLeads([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const stats = useMemo(() => summarizePartnerLeads(leads), [leads]);

  const filteredLeads = useMemo(() => {
    const query = search.trim().toLowerCase();
    return leads.filter((lead) => {
      if (!matchesStatusFilter(lead, statusFilter)) return false;
      if (categoryFilter === "property" && leadCategory(lead.lead_type) !== "property") return false;
      if (categoryFilter === "service" && leadCategory(lead.lead_type) !== "service") return false;
      if (query && !buildLeadSearchHaystack(lead).includes(query)) return false;
      return true;
    });
  }, [leads, search, statusFilter, categoryFilter]);

  const selectedListLead = useMemo(
    () => leads.find((lead) => lead.id === selectedId) || null,
    [leads, selectedId]
  );

  const openLead = useCallback(
    async (id) => {
      setSelectedId(id);
      try {
        setDetailLoading(true);
        setErrorMessage("");
        const body = await getPartnerLead(id);
        setDetail(body.lead);
      } catch (error) {
        setErrorMessage(error.message || t(`${COPY}.errors.detail`));
        setDetail(null);
      } finally {
        setDetailLoading(false);
      }
    },
    [t]
  );

  useEffect(() => {
    if (selectedId || loading || !filteredLeads.length) return;
    const firstNew = filteredLeads.find((lead) => lead.partner_status === "new");
    openLead((firstNew || filteredLeads[0]).id);
  }, [filteredLeads, loading, openLead, selectedId]);

  const updateStatus = async (partnerStatus) => {
    if (!selectedId) return;
    try {
      setSaving(true);
      await updatePartnerLeadStatus(selectedId, { partnerStatus });
      await openLead(selectedId);
      await load();
    } catch (error) {
      setErrorMessage(error.message || t(`${COPY}.errors.update`));
    } finally {
      setSaving(false);
    }
  };

  const submitNote = async () => {
    if (!selectedId || !note.trim()) return;
    try {
      setSaving(true);
      await addPartnerLeadNote(selectedId, note.trim());
      setNote("");
      await openLead(selectedId);
    } catch (error) {
      setErrorMessage(error.message || t(`${COPY}.errors.note`));
    } finally {
      setSaving(false);
    }
  };

  const pipelineMax = Math.max(stats.pipeline.new, stats.pipeline.inProgress, stats.pipeline.closed, 1);

  return (
    <div className="cm-partner-leads partner-leads-page">
      <header className="partner-leads-hero cm-partner-leads__hero">
        <div>
          <p className="cm-partner-leads__eyebrow">{t(`${COPY}.eyebrow`)}</p>
          <h2>{t(`${COPY}.title`)}</h2>
          <p className="partner-dashboard-hero__intro">
            {isVendor ? t(`${COPY}.subtitleVendor`) : t(`${COPY}.subtitleAgency`)}
          </p>
        </div>
        <button type="button" className="partner-dashboard-btn" onClick={load} disabled={loading}>
          {t(`${COPY}.refresh`)}
        </button>
      </header>

      {stats.needsResponseCount > 0 ? (
        <section className="partner-panel cm-partner-leads__callout cm-partner-leads__callout--urgent">
          <div>
            <h2>{t(`${COPY}.priority.title`)}</h2>
            <p>{t(`${COPY}.priority.body`, { count: stats.needsResponseCount })}</p>
          </div>
          <button
            type="button"
            className="partner-dashboard-btn partner-dashboard-btn--primary"
            onClick={() => {
              const next = leads.find((lead) => lead.partner_status === "new");
              if (next) openLead(next.id);
            }}
          >
            {t(`${COPY}.priority.action`)}
          </button>
        </section>
      ) : null}

      <section className="partner-kpi-grid" aria-label={t(`${COPY}.kpiLabel`)}>
        <KpiCard
          label={t(`${COPY}.kpi.new`)}
          value={stats.newCount}
          hint={t(`${COPY}.kpi.newHint`)}
          tone="blue"
        />
        <KpiCard
          label={t(`${COPY}.kpi.needsResponse`)}
          value={stats.needsResponseCount}
          hint={t(`${COPY}.kpi.needsResponseHint`)}
          tone="amber"
        />
        <KpiCard
          label={t(`${COPY}.kpi.inProgress`)}
          value={stats.inProgressCount}
          hint={t(`${COPY}.kpi.inProgressHint`)}
          tone="violet"
        />
        <KpiCard
          label={t(`${COPY}.kpi.closed`)}
          value={stats.closedCount}
          hint={t(`${COPY}.kpi.closedHint`)}
          tone="green"
        />
      </section>

      <div className="cm-partner-leads__overview">
        <section className="partner-panel cm-partner-leads__guidance">
          <h3>{t(`${COPY}.guidance.title`)}</h3>
          <ol>
            <li>
              <strong>{t(`${COPY}.guidance.step1Title`)}</strong>
              {t(`${COPY}.guidance.step1Body`)}
            </li>
            <li>
              <strong>{t(`${COPY}.guidance.step2Title`)}</strong>
              {t(`${COPY}.guidance.step2Body`)}
            </li>
            <li>
              <strong>{t(`${COPY}.guidance.step3Title`)}</strong>
              {t(`${COPY}.guidance.step3Body`)}
            </li>
          </ol>
          {!isVendor ? (
            <p className="muted">
              {t(`${COPY}.guidance.agencyTip`)}{" "}
              <Link to={path("listings")}>{t(`${COPY}.guidance.listingsLink`)}</Link>
            </p>
          ) : (
            <p className="muted">
              {t(`${COPY}.guidance.vendorTip`)}{" "}
              <Link to={path("directory")}>{t(`${COPY}.guidance.directoryLink`)}</Link>
            </p>
          )}
        </section>

        <section className="partner-panel cm-partner-leads__pipeline">
          <h3>{t(`${COPY}.pipeline.title`)}</h3>
          <PipelineBar
            label={t(`${COPY}.pipeline.new`)}
            value={stats.pipeline.new}
            max={pipelineMax}
            tone="blue"
          />
          <PipelineBar
            label={t(`${COPY}.pipeline.inProgress`)}
            value={stats.pipeline.inProgress}
            max={pipelineMax}
            tone="amber"
          />
          <PipelineBar
            label={t(`${COPY}.pipeline.closed`)}
            value={stats.pipeline.closed}
            max={pipelineMax}
            tone="green"
          />
          {stats.propertyCount > 0 && stats.serviceCount > 0 ? (
            <p className="muted">
              {t(`${COPY}.pipeline.mixed`, {
                property: stats.propertyCount,
                service: stats.serviceCount,
              })}
            </p>
          ) : null}
        </section>
      </div>

      {errorMessage ? <p className="admin-data-health-error">{errorMessage}</p> : null}

      <div className="cm-partner-leads__workspace">
        <aside className="partner-leads-sidebar partner-panel">
          <div className="cm-partner-leads__queue-header">
            <h3>{t(`${COPY}.queue.title`)}</h3>
            <p className="cm-partner-leads__queue-hint">{t(`${COPY}.queue.hint`)}</p>
          </div>

          <div className="partner-leads-sidebar__toolbar">
            <input
              type="search"
              className="partner-leads-search"
              placeholder={t(`${COPY}.searchPlaceholder`)}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>

          <div className="cm-partner-leads__filter-row" role="group" aria-label={t(`${COPY}.filters.status`)}>
            {STATUS_FILTER_VALUES.map((value) => {
              const labelKey = value || "all";
              return (
                <button
                  key={labelKey}
                  type="button"
                  className={`cm-partner-leads__chip${statusFilter === value ? " is-active" : ""}`}
                  onClick={() => setStatusFilter(value)}
                >
                  {t(`${COPY}.filters.${labelKey}`)}
                </button>
              );
            })}
          </div>

          {stats.propertyCount > 0 && stats.serviceCount > 0 ? (
            <div className="cm-partner-leads__filter-row" role="group" aria-label={t(`${COPY}.filters.category`)}>
              {["all", "property", "service"].map((value) => (
                <button
                  key={value}
                  type="button"
                  className={`cm-partner-leads__chip${categoryFilter === value ? " is-active" : ""}`}
                  onClick={() => setCategoryFilter(value)}
                >
                  {t(`${COPY}.filters.category_${value}`)}
                </button>
              ))}
            </div>
          ) : null}

          {loading ? <p className="muted partner-leads-sidebar__empty">{t(`${COPY}.loading`)}</p> : null}

          {!loading && filteredLeads.length === 0 ? (
            <div className="partner-leads-sidebar__empty cm-partner-leads__empty-state">
              <h3>{leads.length ? t(`${COPY}.emptyFilteredTitle`) : t(`${COPY}.emptyTitle`)}</h3>
              <p>{leads.length ? t(`${COPY}.emptyFilteredBody`) : t(`${COPY}.emptyBody`)}</p>
              {!leads.length && !isVendor ? (
                <Link className="partner-dashboard-btn" to={path("listings")}>
                  {t(`${COPY}.emptyListingsAction`)}
                </Link>
              ) : null}
              {!leads.length && isVendor ? (
                <Link className="partner-dashboard-btn" to={path("directory")}>
                  {t(`${COPY}.emptyDirectoryAction`)}
                </Link>
              ) : null}
            </div>
          ) : null}

          {!loading && filteredLeads.length > 0 ? (
            <div className="partner-leads-list">
              {filteredLeads.map((lead) => (
                <LeadListItem
                  key={lead.id}
                  lead={lead}
                  selected={selectedId === lead.id}
                  onSelect={openLead}
                  t={t}
                  locale={locale}
                />
              ))}
            </div>
          ) : null}
        </aside>

        <section className="partner-leads-main partner-panel">
          {detailLoading ? <p className="muted">{t(`${COPY}.detail.loading`)}</p> : null}
          {!detailLoading && detail ? (
            <LeadDetailPanel
              detail={detail}
              listMeta={parseLeadMetadata(selectedListLead)}
              note={note}
              onNoteChange={setNote}
              onSubmitNote={submitNote}
              onStatusChange={updateStatus}
              onApplyTemplate={setNote}
              templates={templates}
              saving={saving}
              t={t}
              locale={locale}
            />
          ) : null}
          {!detailLoading && !detail ? <LeadDetailEmpty t={t} /> : null}
        </section>
      </div>
    </div>
  );
}
